const BASE = import.meta.env.VITE_API_BASE ?? "";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Sent on every call. The charged portal intel routes refuse a request without
 * it (servePortal in src/lib/intel/serve.ts, CE-175): another site can make the
 * browser send our cookie on a link or a redirect, but cannot add a header.
 */
const PORTAL_HEADER = { "X-CE-Portal": "1" };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    // Same-origin httpOnly ce_* cookies ride along automatically — no tokens in
    // JS, no Authorization header. This is the whole reason the hybrid is safe.
    credentials: "include",
    ...init,
    headers: { Accept: "application/json", ...PORTAL_HEADER, ...(init?.headers ?? {}) },
  });

  const ct = res.headers.get("content-type") ?? "";
  const data = ct.includes("application/json")
    ? await res.json().catch(() => null)
    : await res.text();

  if (!res.ok) {
    throw new ApiError(res.status, `${init?.method ?? "GET"} ${path} -> ${res.status}`, data);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

export default api;
